import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isValid,
  parse,
  startOfMonth,
  startOfWeek,
} from "date-fns";

/**
 * 週の始まり（0=日曜 … 1=月曜）。
 *
 * 出荷が立つのは平日なので、土日が右端に寄る月曜始まりにしている。日曜始まりに
 * したいときはここだけ変えれば曜日の見出しも一緒に動く。
 */
export const WEEK_STARTS_ON = 1 as const;

const DAY_LABELS = ["日", "月", "火", "水", "木", "金", "土"];

/** 曜日の見出し（週の始まりに合わせて回してある）。 */
export const WEEKDAY_LABELS = [
  ...DAY_LABELS.slice(WEEK_STARTS_ON),
  ...DAY_LABELS.slice(0, WEEK_STARTS_ON),
];

/** 月の鍵（`2026-10`）。URL のクエリにそのまま載せる。 */
export function monthKey(date: Date): string {
  return format(date, "yyyy-MM");
}

/** 日付の鍵（`2026-10-30`）。API の `ship_date` と同じ形。 */
export function dateKey(date: Date): string {
  return format(date, "yyyy-MM-dd");
}

/**
 * 月の鍵を Date に戻す。壊れた値（手で URL を直した場合）は null。
 *
 * `new Date("2026-10")` は環境によって解釈が揺れるので date-fns で明示的に読む。
 */
export function parseMonthKey(key: string): Date | null {
  const parsed = parse(key, "yyyy-MM", new Date());
  return isValid(parsed) ? startOfMonth(parsed) : null;
}

/** 月を送る（`delta` が負なら戻る）。 */
export function shiftMonth(key: string, delta: number): string {
  const month = parseMonthKey(key) ?? startOfMonth(new Date());
  return monthKey(addMonths(month, delta));
}

/** 見出しに出す月（`2026年10月`）。 */
export function monthLabel(key: string): string {
  const month = parseMonthKey(key);
  return month ? format(month, "yyyy年M月") : key;
}

/**
 * その月のグリッドに並べる日を返す（35 日または 42 日）。
 *
 * 前月末と翌月頭のマスを含むので、API に渡す期間は**この配列の両端**から取る
 * （月の初日・末日ではカレンダーの端のマスが空になる）。
 */
export function monthGridDays(key: string): Date[] {
  const month = parseMonthKey(key) ?? startOfMonth(new Date());
  return eachDayOfInterval({
    start: startOfWeek(startOfMonth(month), { weekStartsOn: WEEK_STARTS_ON }),
    end: endOfWeek(endOfMonth(month), { weekStartsOn: WEEK_STARTS_ON }),
  });
}

/** 土日か（地の色を変えるため。祝日は今は見ていない）。 */
export function isWeekend(date: Date): boolean {
  const day = date.getDay();
  return day === 0 || day === 6;
}
